import { useState } from 'react'
import { List, Tag, Button, Typography, Popconfirm, message } from 'antd'
import { FilePdfOutlined, FileImageOutlined, EyeOutlined, DeleteOutlined, CheckCircleOutlined, EditOutlined, DownloadOutlined } from '@ant-design/icons'
import { abrirArchivo } from '../api'
import FirmaDocumento from './FirmaDocumento'

const { Text } = Typography

// Compartido entre la gestión de trabajadores (admin) y "Mi perfil" (trabajador).
// firma: si al subirlo se propone pedir firma al trabajador (decisión 30/10/2026)
export const CATEGORIAS_DOC = [
  { value: 'nomina', label: 'Nómina', color: 'green', firma: true },
  { value: 'contrato', label: 'Contrato y anexos', color: 'blue', firma: true },
  { value: 'jornadas', label: 'Registro de jornadas', color: 'purple', firma: false },
  { value: 'epis', label: 'Entrega de EPIs', color: 'orange', firma: true },
  { value: 'otros', label: 'Otros', color: 'default', firma: false },
]

export const categoriaInfo = (c) => CATEGORIAS_DOC.find(x => x.value === c) || CATEGORIAS_DOC[4]

const fechaHora = (f) => f ? new Date(f).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''

// modo 'admin': enseña si el trabajador lo ha abierto y firmado, deja borrar y pedir firma.
// modo 'propio': marca como "Nuevo" lo que el trabajador aún no ha abierto; lo que
// pide firma se ve en pantalla y no se descarga hasta firmarlo.
export function ListaDocumentos({ documentos, modo, onCambio, onBorrar, onPedirFirma }) {
  const [visor, setVisor] = useState(null)
  const pendiente = (doc) => doc.requiere_firma && !doc.firmado

  const descargar = async (doc) => {
    try {
      await abrirArchivo(`/documentos-trabajador/${doc.id}/descargar`)
      if (modo === 'propio' && !doc.visto && onCambio) onCambio()
    } catch (err) {
      message.error(err.message)
    }
  }

  const abrir = (doc) => {
    if (modo === 'propio' && pendiente(doc)) setVisor(doc)
    else descargar(doc)
  }

  const cerrarVisor = () => {
    const eraNuevo = visor && !visor.visto
    setVisor(null)
    if (eraNuevo && onCambio) onCambio()
  }

  return (
    <>
      <List
        dataSource={documentos}
        locale={{ emptyText: 'No hay documentos' }}
        renderItem={doc => {
          const cat = categoriaInfo(doc.categoria)
          const esImagen = (doc.tipo_mime || '').startsWith('image/') && !doc.firmado
          const acciones = []
          if (modo === 'propio' && pendiente(doc)) {
            acciones.push(<Button key="firmar" type="primary" size="small" icon={<EditOutlined />} onClick={() => setVisor(doc)} style={{ background: '#2E7D32' }}>Ver y firmar</Button>)
          } else {
            acciones.push(<Button key="ver" type="link" icon={doc.firmado ? <DownloadOutlined /> : <EyeOutlined />} onClick={() => descargar(doc)}>{doc.firmado ? 'Descargar' : 'Ver'}</Button>)
          }
          if (modo === 'admin') {
            acciones.push(
              <Popconfirm key="borrar" title="¿Eliminar este documento?"
                description={doc.firmado ? 'Está firmado: se perderá también la firma, que es la prueba de que lo recibió.' : 'Se borra también el archivo y el trabajador dejará de verlo.'}
                onConfirm={() => onBorrar(doc)} okText="Eliminar" okButtonProps={{ danger: true }} cancelText="No">
                <Button type="text" danger size="small" icon={<DeleteOutlined />} />
              </Popconfirm>
            )
          }
          return (
            <List.Item style={{ padding: '10px 0' }} actions={acciones}>
              <List.Item.Meta
                avatar={esImagen
                  ? <FileImageOutlined style={{ fontSize: '22px', color: '#2E5FA3' }} />
                  : <FilePdfOutlined style={{ fontSize: '22px', color: '#C62828' }} />}
                title={
                  <span style={{ cursor: 'pointer' }} onClick={() => abrir(doc)}>
                    <Text strong style={{ fontSize: '13px' }}>{doc.nombre}</Text>
                    {modo === 'propio' && !doc.visto && <Tag color="red" style={{ marginLeft: '6px', fontSize: '10px' }}>Nuevo</Tag>}
                  </span>
                }
                description={
                  <div style={{ fontSize: '11px' }}>
                    <Tag color={cat.color} style={{ fontSize: '10px' }}>{cat.label}</Tag>
                    {pendiente(doc) && <Tag color="warning" style={{ fontSize: '10px' }}>Pendiente de firma</Tag>}
                    {doc.firmado && <Tag color="success" style={{ fontSize: '10px' }}>Firmado</Tag>}
                    <Text type="secondary" style={{ fontSize: '11px' }}>{fechaHora(doc.fecha_subida)}</Text>
                    {modo === 'admin' && (
                      <div style={{ marginTop: '2px' }}>
                        {doc.visto
                          ? <Text style={{ fontSize: '11px', color: '#2E7D32' }}><CheckCircleOutlined /> Abierto por el trabajador el {fechaHora(doc.fecha_visto)}</Text>
                          : <Text type="secondary" style={{ fontSize: '11px' }}>Aún no lo ha abierto</Text>}
                        {doc.firmado && (
                          <div><Text style={{ fontSize: '11px', color: '#2E7D32' }}><EditOutlined /> Firmado el {fechaHora(doc.fecha_firma)}</Text></div>
                        )}
                        {!doc.firmado && onPedirFirma && (
                          <div>
                            <Button type="link" size="small" style={{ padding: 0, fontSize: '11px', height: 'auto' }} onClick={() => onPedirFirma(doc, !doc.requiere_firma)}>
                              {doc.requiere_firma ? 'No pedir firma' : 'Pedir firma'}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                }
              />
            </List.Item>
          )
        }}
      />
      {visor && (
        <FirmaDocumento
          documento={visor}
          onCerrar={cerrarVisor}
          onFirmado={() => { setVisor(null); if (onCambio) onCambio() }}
        />
      )}
    </>
  )
}
